
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRouter } from 'vue-router'

// Components
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// Classes
import { DataState } from '@/core/classes/DataState.class'

// Types
import { TmcSecondaryMaterialItem, TmcSecondaryMaterialPayload, TmcT } from '@/core/types/tmc.types'
import { WarehouseT } from '@/core/types/warehouses.types'

// Realisations
import { tmc } from '@/core/realisations/tmc.realisation'
import { warehouses } from '@/core/realisations/warehouses.realisation'

// Validation
import useVuelidate from '@vuelidate/core'
import { required, integer, requiredIf } from '@vuelidate/validators'

// Composable
import useHelpers from '@/common/composable/useHelpers'
import useNotifications from '@/common/composable/useNotifications'
import { useRound } from '@/common/composable/useRound'
import { CompanyT } from '@/core/types/companies.types'
import { companies } from '@/core/realisations/companies.realisation'

export default defineComponent({
    components: {
        'the-button': TheButton,
        'the-input': TheInput,
        'the-select': TheSelect,
    },
    setup() {
        const router = useRouter()
        const { findItemIndexById } = useHelpers()
        const notifications = useNotifications()
        const { roundTo } = useRound()

        const state = reactive({
            warehouseId: null as number | null,
            palletAmount: null as number | null,
            //
            items: [] as {
                tmc: number
                tmc_name: string
                amount: number
                companyId: number
            }[],
            //
            is_loading: false,
        })

        const data_state = reactive({
            warehouses: new DataState<WarehouseT>({}),
            tmc: new DataState<TmcT>({}),
            companies: new DataState<CompanyT>({}),
        })

        const item_state = reactive({
            tmc: null as number | null,
            amount: null as string | null,
            companyId: null as number | null,
        })

        const state_validation = useVuelidate(
            {
                warehouseId: { required },
                palletAmount: { required, integer },
                items: { required },
            },
            state
        )

        const isClient = computed(() => item_state.tmc === 252)

        const item_validation = useVuelidate(
            {
                tmc: { required },
                amount: { required, integer },
                companyId: { required: requiredIf(() => isClient.value) },
            },
            item_state
        )

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    statusId: 2,
                    isExtended: 0,
                    limit: 9999,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        // TMC

        const loadTMC = async (page: number, search?: string) => {
            data_state.tmc.pagination.current_page = page

            try {
                data_state.tmc.is_loading = true

                let params = {
                    id: null,
                    name: null,
                    code1C: null,
                    unitId: null,
                    isSecondMaterial: 1,
                }

                const response = await tmc.getList(page, search, params)
                data_state.tmc.list = response.data

                data_state.tmc.pagination.total_pages = response.meta.last_page

                data_state.tmc.is_failed = false
                data_state.tmc.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.tmc.failure.catchError(error)

                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_failed = true
                }
            } finally {
                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_loading = false
                }
            }
        }

        const loadMoreTMC = async (page: number) => {
            if (page > data_state.tmc.pagination.total_pages) return
            data_state.tmc.pagination.current_page = page

            try {
                data_state.tmc.is_loading = true

                const response = await tmc.getList(page)
                data_state.tmc.list = data_state.tmc.list.concat(response.data)

                data_state.tmc.pagination.total_pages = response.meta.last_page

                data_state.tmc.is_failed = false
                data_state.tmc.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.tmc.failure.catchError(error)

                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_failed = true
                }
            } finally {
                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_loading = false
                }
            }
        }

        const searchTMC = (search_query: string) => {
            loadTMC(1, search_query)
        }

        const unshiftFindedTMC = async (tmc: TmcT) => {
            await loadTMC(data_state.tmc.pagination.current_page)
            data_state.tmc.list.unshift(tmc)
        }

        // Warehouses
        const loadWarehouses = async () => {
            data_state.warehouses.pagination.current_page = 1

            try {
                data_state.warehouses.is_loading = true
                const response = await warehouses.getWarehouses()
                data_state.warehouses.list = response.data

                // data_state.warehouses.pagination.total_pages = response.meta.last_page

                data_state.warehouses.is_failed = false
                data_state.warehouses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.warehouses.failure.catchError(error)

                if (!data_state.warehouses.failure.error_state.is_cancel) {
                    data_state.warehouses.is_failed = true
                }
            } finally {
                if (!data_state.warehouses.failure.error_state.is_cancel) {
                    data_state.warehouses.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadCompanies(1)
            loadTMC(1)
            loadWarehouses()
        })

        const resetItemForm = () => {
            item_state.tmc = null
            item_state.amount = null
            item_state.companyId = null

            item_validation.value.$reset()
        }

        const addItem = () => {
            item_validation.value.$touch()
            if (item_validation.value.$invalid) return

            const index = findItemIndexById(item_state.tmc as number, data_state.tmc.list)
            if (index < 0) return

            const tmc_name = data_state.tmc.list[index].name
            const maybe_selected_tmc = state.items.find((item) => item.tmc === item_state.tmc)

            if (maybe_selected_tmc) {
                notifications.notify({
                    title: `ТМЦ '${tmc_name}' уже выбран`,
                    description: 'Пожалуйста, выберите другой ТМЦ',
                    type: 'danger',
                })
                return
            }

            const item = {
                tmc: item_state.tmc as number,
                tmc_name,
                amount: Number(item_state.amount as string),
                companyId: item_state.companyId as number,
            }

            state.items.push(item)
            resetItemForm()
        }

        const removeItem = (item_index: number) => {
            state.items.splice(item_index, 1)
        }

        const mapItems = (item: {
            tmc: number
            tmc_name: string
            amount: number
            companyId: number
        }): TmcSecondaryMaterialItem => {
            return {
                inventoryItemId: item.tmc,
                amount: item.amount,
                companyId: item.companyId,
            }
        }

        const submitForm = async () => {
            if (state.is_loading) return

            if (
                state.items.length === 0 ||
                (state.items.length > 0 && (item_state.tmc || item_state.amount))
            ) {
                addItem()
            }

            state_validation.value.$touch()

            if (state_validation.value.$invalid) {
                if (state.items.length === 0) {
                    notifications.notify({
                        title: 'Ошибка валидации',
                        description: 'Для создания заявки необходимо добавить хотя бы 1 товар',
                        type: 'danger',
                    })
                }

                return
            }

            try {
                state.is_loading = true

                const payload: TmcSecondaryMaterialPayload = {
                    warehouseId: state.warehouseId as number,
                    palletAmount: Number(state.palletAmount) as number,
                    receiptItems: state.items.map(mapItems),
                }

                await tmc.createSecondaryMaterial(payload)

                notifications.notify({
                    title: 'Поступление вторичного материала создано',
                    type: 'success',
                })
                router.push('/tmc/admission')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    title: 'При создании поступления произошла ошибка',
                    error,
                })
            } finally {
                state.is_loading = false
            }
        }

        const onTmcChange = () => {
            if (!isClient.value) {
                item_state.companyId = null
            }
        }

        return {
            state,
            data_state,
            item_state,
            //
            state_validation,
            item_validation,
            //
            loadTMC,
            loadMoreTMC,
            searchTMC,
            unshiftFindedTMC,
            loadWarehouses,
            //
            addItem,
            removeItem,
            submitForm,
            //
            roundTo,
            isClient,
            loadCompanies,
            onTmcChange,
        }
    },
})
